import React from "react";
import { Link } from "react-router-dom";
import { IoIosArrowForward } from "react-icons/io";
import { FormatNumber } from "../../helpers";
import { useDebts, useDebtParties, KIND_UTANG, KIND_PIUTANG } from "../../api";
import "./party.css";

const initials = (n) =>
  (n || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/**
 * Aggregates parties from /debts so we don't need a dedicated list endpoint.
 * For each party: pulls all their debts (kind any) → computes net outstanding,
 * count of active debts, and a one-line summary.
 */
const PartyList = ({ search = "" }) => {
  const { parties } = useDebtParties();
  const { debts: utangs } = useDebts({ kind: KIND_UTANG });
  const { debts: piutangs } = useDebts({ kind: KIND_PIUTANG });

  // Aggregate per party
  const agg = React.useMemo(() => {
    const map = new Map();
    const seed = (pid, p) =>
      map.set(pid, {
        id: pid,
        party: p,
        utang: 0,
        piutang: 0,
        utangPaid: 0,
        piutangPaid: 0,
        active: 0,
        total: 0,
      });
    parties.forEach((p) => seed(p.id, p));
    const addRow = (rows, kind) => {
      rows.forEach((d) => {
        if (!map.has(d.party.id)) seed(d.party.id, d.party);
        const e = map.get(d.party.id);
        if (kind === KIND_UTANG) {
          e.utang += d.outstanding;
          e.utangPaid += d.paidAmount;
        } else {
          e.piutang += d.outstanding;
          e.piutangPaid += d.paidAmount;
        }
        e.total += 1;
        if (["open", "partial", "overdue"].includes(d.status)) e.active += 1;
      });
    };
    addRow(utangs, KIND_UTANG);
    addRow(piutangs, KIND_PIUTANG);
    return Array.from(map.values()).sort(
      (a, b) =>
        Math.abs(b.piutang - b.utang) - Math.abs(a.piutang - a.utang)
    );
  }, [parties, utangs, piutangs]);

  const filtered = agg.filter((a) =>
    a.party.name.toLowerCase().includes(search.toLowerCase())
  );

  if (filtered.length === 0) {
    return (
      <div className="debt-empty">
        <div className="debt-empty__title">Belum ada counterparty</div>
        <p className="debt-empty__sub">
          Counterparty muncul otomatis saat Anda catat utang/piutang baru.
        </p>
      </div>
    );
  }

  return (
    <>
      {filtered.map((e) => {
        const net = e.piutang - e.utang;
        const cls = net > 0 ? "owed" : net < 0 ? "you-owe" : "even";
        const direction =
          net > 0
            ? "berutang kepada Anda"
            : net < 0
            ? "Anda berutang"
            : "Lunas";
        return (
          <Link
            to={`/debts/parties/${e.id}`}
            key={e.id}
            className="pty-list-card"
          >
            <div className="pty-list-card__avatar">
              {initials(e.party.name)}
            </div>
            <div className="pty-list-card__body">
              <div className="pty-list-card__name">{e.party.name}</div>
              <div className={`pty-list-card__net pty-list-card__net--${cls}`}>
                {net !== 0 && (net > 0 ? "+" : "−")}Rp{" "}
                {FormatNumber(Math.round(Math.abs(net)))} · {direction}
              </div>
              <div className="pty-list-card__meta">
                {e.total} transaksi · {e.active} aktif
              </div>
            </div>
            <IoIosArrowForward className="pty-list-card__chevron" />
          </Link>
        );
      })}
    </>
  );
};

export default PartyList;
